import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, FormBuilder } from '@angular/forms';
import {
  AgpFormUtil,
  CvaFormGroupComponent,
  ResetOptions
} from '@liberty-financial/agp-shared';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { Observable, Observer, of } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';

import { TypeaheadOption } from '../typeahead-option';

interface TypeaheadForm<T> {
  option: TypeaheadOption<T>;
  search: unknown;
}

@Component({
  selector: 'spark-shared-form-typeahead-field',
  templateUrl: './typeahead-field.component.html',
  styleUrls: ['./typeahead-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SparkSharedFormTypeaheadFieldComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => SparkSharedFormTypeaheadFieldComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None
})
export class SparkSharedFormTypeaheadFieldComponent<T>
  extends CvaFormGroupComponent<TypeaheadOption<T>, TypeaheadForm<T>>
  implements OnInit, OnChanges
{
  @Input() name: string = '';
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() maxLength: number = 500;
  @Input() hasErrors: boolean = false;
  @Input() isAsync = true;
  @Input() dataSource: TypeaheadOption<T>[] = [];
  @Input() dataSource$: (q: string) => Observable<TypeaheadOption<T>[]> = (_) =>
    of(this.dataSource);
  @Input() isDisabled: boolean = false;
  @Input() waitMs: number = 300;
  @Input() minLength: number = 0;
  @Input() isFirstItemActive: boolean = false;
  @Input() isRequired: boolean = false;
  @Input() showHighlight: boolean = false;
  @Input() messages: { [index: string]: string } = {};
  @Input() allowManualEntry = false;

  searchInProgress: boolean = false;
  search: string = '';

  source$: Observable<TypeaheadOption<T>[]> = new Observable(
    (observer: Observer<string>) => {
      observer.next(this.search);
    }
  ).pipe(
    tap((_) => (this.searchInProgress = true)),
    switchMap((query) => {
      return (typeof query === 'string' && query === '') || query === null
        ? of([])
        : this.dataSource$(query);
    }),
    tap((_) => {
      this.searchInProgress = false;
    })
  );

  form = this.fb.control<TypeaheadOption<T> | null>(null);
  formGroup = this.fb.group({
    option: this.form,
    search: this.fb.control<unknown>(null)
  });

  get hasLabel() {
    return !!this.label;
  }

  constructor(private readonly fb: FormBuilder) {
    super();
  }

  protected reset(opts?: ResetOptions | undefined): void {
    this.form.reset(undefined, opts);
  }

  ngOnInit() {
    this.formGroup.controls.search.valueChanges
      .pipe(takeUntil(this.destroying$))
      .subscribe((value) => (this.search = value as string));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isRequired) {
      AgpFormUtil.Validators.updateRequiredIfNecessary(this.form, changes.isRequired.currentValue);
    }
  }

  onSelect(event: TypeaheadMatch): void {
    this.form.patchValue(event.item);
  }

  typeaheadOnBlur(event: TypeaheadMatch): void {
    if (event) {
      this.form.patchValue(event.item);
    }
  }

  onBlur(event: any): void {
    if (this.allowManualEntry) {
      this.form.setValue(event.target.value);
    }
  }

  protected adjustFormValue(
    value: TypeaheadOption<T>,
    opts?: ResetOptions | undefined
  ): void {
    this.formGroup.controls.search.patchValue(value.label);
  }

  public get hasSearchError() {
    return this.form.invalid && this.formGroup.controls.search.touched;
  }
}
